import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import useAuthStore from '../store/authStore';
import SEOHead from '../components/SEOHead';
import { useDarkMode } from '../contexts/DarkModeContext';

// const steps = [
//   'Account Info',
//   'Upload Images',
//   'Account Stats',
// ];

// Social media platform icons
const platformIcons = {
  facebook: (
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600">
      <FaFacebookF className="text-white text-2xl" />
    </span>
  ),
  instagram: (
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-yellow-500">
      <FaInstagram className="text-white text-2xl" />
    </span>
  ),
  tiktok: (
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-black">
      <FaTiktok className="text-white text-2xl" />
    </span>
  ),
  youtube: (
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-red-600">
      <FaYoutube className="text-white text-2xl" />
    </span>
  ),
};

// Modern input class for all fields
const modernInput = "w-full bg-gray-50 dark:bg-[#2a2a2a] border border-gray-200 dark:border-gray-600 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-base text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 transition-all duration-200 shadow-sm hover:border-blue-300 dark:hover:border-blue-400 hover:shadow-md";

// function Stepper({ step }) {
//   return (
//     <div className="flex items-center justify-center gap-4 mb-8">
//       {steps.map((label, idx) => (
//         <React.Fragment key={label}>
//           <div className={`flex flex-col items-center ${step === idx ? 'text-blue-600' : 'text-gray-400'}`}>
//             <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step === idx ? 'border-blue-600 bg-white' : 'border-gray-300 bg-gray-100'} text-lg font-bold`}>{idx + 1}</div>
//             <span className="mt-2 text-xs font-medium">{label}</span>
//           </div>
//           {idx < steps.length - 1 && <div className="flex-1 h-1 bg-gray-200 mx-2" />}
//         </React.Fragment>
//       ))}
//     </div>
//   );
// }

function SellAccount() {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { user } = useAuthStore();
  const mainImageInputRef = useRef();
  const galleryInputRef = useRef();
  const [seoSettings, setSeoSettings] = useState(null);

  // Fetch SEO settings for sell account page
  useEffect(() => {
    fetch('/api/seo?path=/sell-account')
      .then(res => res.json())
      .then(data => setSeoSettings(data))
      .catch(error => {
        // Handle error silently for production
        setSeoSettings(null);
      });
  }, []);
  const mainImageContainerRef = useRef();
  const galleryImagesContainerRef = useRef();
  const fieldRefs = {
    accountType: useRef(),
    accountName: useRef(),
    accountUrl: useRef(),
    price: useRef(),
    salePrice: useRef(),
    description: useRef(),
    mainImage: mainImageContainerRef,
    galleryImages: galleryImagesContainerRef,
    followers: useRef(),
    monetized: useRef(),
    category: useRef(),
    income: useRef(),
    videos: useRef(),
    views: useRef(),
    strikes: useRef(),
    created: useRef(),
  };
  const initialFormState = {
    accountType: '',
    accountName: '',
    accountUrl: '',
    price: '',
    salePrice: '',
    description: '',
    mainImage: null,
    mainImagePreview: '',
    galleryImages: [],
    galleryPreviews: [],
    stats: {
      followers: '',
      monetized: '',
      income: '',
      videos: '',
      category: '',
      views: '',
      strikes: '',
      created: '',
    },
  };
  const [form, setForm] = useState(initialFormState);
  const [submitted, setSubmitted] = useState(false);
  const [thankYou, setThankYou] = useState('');
  const [productId, setProductId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [mainImageDragActive, setMainImageDragActive] = useState(false);
  const [galleryDragActive, setGalleryDragActive] = useState(false);


  // Sanitize input to prevent XSS
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input.replace(/[<>]/g, '').trim();
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Account type validation
    if (!form.accountType) errors.accountType = 'Please select an account type';
    
    // Account name validation
    if (!form.accountName) errors.accountName = 'Account name is required';
    else if (form.accountName.trim().length < 3) errors.accountName = 'Account name must be at least 3 characters';
    
    // Account URL validation
    if (!form.accountUrl) errors.accountUrl = 'Account URL is required';
    else if (!form.accountUrl.includes('http') && !form.accountUrl.includes('www')) {
      errors.accountUrl = 'Please enter a valid URL (e.g., https://instagram.com/youraccount)';
    }
    
    // Price validation
    if (!form.price) errors.price = 'Price is required';
    else if (isNaN(form.price) || Number(form.price) <= 0) errors.price = 'Please enter a valid price';
    
    // Sale price validation
    if (form.salePrice) {
      if (isNaN(form.salePrice) || Number(form.salePrice) <= 0) {
        errors.salePrice = 'Please enter a valid sale price';
      } else if (Number(form.salePrice) >= Number(form.price)) {
        errors.salePrice = 'Sale price must be less than regular price';
      }
    }
    
    // Description validation
    if (!form.description) errors.description = 'Description is required';
    else if (form.description.trim().length < 10) errors.description = 'Description must be at least 10 characters';
    else if (form.description.trim().length > 500) errors.description = 'Description must be less than 500 characters';
    
    // Main image validation
    if (!form.mainImage) errors.mainImage = 'Main image is required';
    
    // Gallery images validation
    if (form.galleryImages.length < 3) errors.galleryImages = 'At least 3 gallery images are required';
    
    // Stats validation
    if (!form.stats.followers) errors.followers = 'Number of followers is required';
    else if (isNaN(form.stats.followers) || Number(form.stats.followers) < 0) {
      errors.followers = 'Please enter a valid number of followers';
    }
    
    if (!form.stats.monetized) errors.monetized = 'Please specify if the account is monetized';
    
    if (!form.stats.category) errors.category = 'Please select a category';
    
    if (!form.stats.income) errors.income = 'Monthly income is required';
    else if (isNaN(form.stats.income) || Number(form.stats.income) < 0) {
      errors.income = 'Please enter a valid monthly income';
    }
    
    if (!form.stats.videos) errors.videos = 'Number of videos is required';
    else if (isNaN(form.stats.videos) || Number(form.stats.videos) < 0) {
      errors.videos = 'Please enter a valid number of videos';
    }
    
    if (!form.stats.views) errors.views = 'Total views is required';
    else if (isNaN(form.stats.views) || Number(form.stats.views) < 0) {
      errors.views = 'Please enter a valid number of views';
    }
    
    if (!form.stats.strikes) errors.strikes = 'Please specify if the account has strikes';
    
    if (!form.stats.created) errors.created = 'Account creation date is required';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Image handlers
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      if (file.size > 20 * 1024 * 1024) { // Updated to 20MB limit
        setError('Main image must be less than 20MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(f => ({ ...f, mainImage: file, mainImagePreview: ev.target.result }));
        setValidationErrors(prev => ({ ...prev, mainImage: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryChange = (e) => {
    let files = Array.from(e.target.files);
    
    // Validate file types
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      setError('Please select valid image files only');
      return;
    }
    
    // Filter out files already in galleryImages by name
    const existingNames = form.galleryImages.map(f => f.name);
    files = files.filter(f => !existingNames.includes(f.name));
    // Merge with existing images, limit to 15
    let newGalleryImages = [...form.galleryImages, ...files].slice(0, 15);
    // Check file sizes - updated to 20MB
    const oversizedFiles = newGalleryImages.filter(file => file.size > 20 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Some images are larger than 20MB. Please resize them before uploading.');
      return;
    }
    // Generate previews for all images
    Promise.all(newGalleryImages.map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ file, preview: ev.target.result });
        reader.readAsDataURL(file);
      });
    })).then(results => {
      setForm(f => ({
        ...f,
        galleryImages: results.map(r => r.file),
        galleryPreviews: results.map(r => r.preview),
      }));
      setValidationErrors(prev => ({ ...prev, galleryImages: '' }));
    });
  };

  // Drag and drop handlers for main image
  const handleMainImageDragOver = (e) => {
    e.preventDefault();
    setMainImageDragActive(true);
  };
  const handleMainImageDragLeave = (e) => {
    e.preventDefault();
    setMainImageDragActive(false);
  };
  const handleMainImageDrop = (e) => {
    e.preventDefault();
    setMainImageDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Only take the first file for main image
      const file = e.dataTransfer.files[0];
      handleMainImageChange({ target: { files: [file] } });
    }
  };
  // Drag and drop handlers for gallery images
  const handleGalleryDragOver = (e) => {
    e.preventDefault();
    setGalleryDragActive(true);
  };
  const handleGalleryDragLeave = (e) => {
    e.preventDefault();
    setGalleryDragActive(false);
  };
  const handleGalleryDrop = (e) => {
    e.preventDefault();
    setGalleryDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleGalleryChange({ target: { files: e.dataTransfer.files } });
    }
  };

  // Remove main image handler
  const handleRemoveMainImage = () => {
    setForm(f => ({ ...f, mainImage: null, mainImagePreview: '' }));
  };
  // Remove gallery image handler
  const handleRemoveGalleryImage = (idx) => {
    setForm(f => ({
      ...f,
      galleryImages: f.galleryImages.filter((_, i) => i !== idx),
      galleryPreviews: f.galleryPreviews.filter((_, i) => i !== idx),
    }));
  };

  // Submit handler
  const handleSubmit = async (e, status = 'pending') => {
    e.preventDefault();
    setError('');
    setThankYou('');
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey && fieldRefs[firstErrorKey]?.current) {
        fieldRefs[firstErrorKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs[firstErrorKey].current.focus?.();
      }
      return;
    }

    // Check if user is logged in using the auth store
    if (!user) {
      setError('You must be logged in to publish a product.');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', sanitizeInput(form.accountName));
      fd.append('description', sanitizeInput(form.description));
      fd.append('price', Number(form.price));
      if (form.salePrice) fd.append('salePrice', Number(form.salePrice));
      fd.append('accountType', form.accountType);
      fd.append('category', form.accountType);
      fd.append('accountUrl', sanitizeInput(form.accountUrl));
      fd.append('status', status);
      fd.append('mainImage', form.mainImage);
      form.galleryImages.forEach((img, idx) => fd.append('galleryImages', img));
      Object.entries(form.stats).forEach(([key, value]) => fd.append(`stats[${key}]`, sanitizeInput(value)));

      
      const res = await fetch('/api/products', {
        method: 'POST',
        credentials: 'include', // Use session cookies instead of token
        body: fd,
      });
      const rawText = await res.text();
      let data;
      try { data = rawText ? JSON.parse(rawText) : {}; } catch { data = { message: rawText }; }
      if (res.ok) {
        setThankYou(`Thank you! Your product has been published. Product ID: ${data.productId}`);
        setProductId(data.productId);
        setSubmitted(true);
        setShowThankYouModal(true);
      } else {
        // Handle validation errors with specific field messages
        if (data.details && Array.isArray(data.details)) {
          const errorMessages = data.details.map(error => {
            const fieldName = error.field || 'field';
            return `• ${fieldName}: ${error.message}`;
          }).join('\n');
          setError(`Please fix the following errors:\n${errorMessages}`);
        } else {
          setError(data.message || data.error?.message || 'Failed to publish product.');
        }
      }
    } catch (err) {
      setError('Failed to publish product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Modern single-page form
  return (
    <PageLayout>
      <SEOHead
        title={seoSettings?.title || 'Sell Social Media Account - YTProud'}
        description={seoSettings?.description || 'Sell your social media accounts securely. Get the best price for your YouTube, TikTok, Instagram, and Facebook accounts.'}
        keywords={seoSettings?.keywords || 'sell social media account, sell youtube channel, sell tiktok account'}
        ogTitle={seoSettings?.ogTitle || 'Sell Social Media Account - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Sell your social media accounts securely'}
        ogImage={seoSettings?.ogImage || '/images/sell-og.jpg'}
        twitterTitle={seoSettings?.twitterTitle || 'Sell Social Media Account - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Sell your social media accounts on YTProud'}
        twitterImage={seoSettings?.twitterImage || '/images/sell-account-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/sell-account'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
      />
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-4 sm:py-6 lg:py-10">
        <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
          {/* Thank You Modal Popup */}
          {showThankYouModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
              <div className="relative bg-white dark:bg-[#2a2a2a] rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-12 max-w-[700px] w-full mx-auto text-center border border-gray-100 dark:border-gray-700">
                {/* Close Icon */}
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold focus:outline-none"
                  onClick={() => {
                    setShowThankYouModal(false);
                    setForm(initialFormState);
                    setValidationErrors({});
                    setError('');
                    setThankYou('');
                    setProductId('');
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
                <h2 className="text-2xl sm:text-3xl font-bold text-green-600 mb-3 sm:mb-4">Thank you!</h2>
                <p className="text-base sm:text-lg mb-2 text-gray-900 dark:text-gray-100">Your product has been published.</p>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-4">Product ID: <span className="font-mono text-blue-600 dark:text-blue-400">{productId}</span></p>
                <button className="mt-4 sm:mt-6 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition text-sm sm:text-base" onClick={() => navigate('/profile')}>Go to My Listings</button>
              </div>
            </div>
          )}
          {/* End Thank You Modal */}
          {!showThankYouModal && (
            <form className={`bg-white dark:bg-[#2a2a2a] rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-12 w-full mx-auto space-y-8 sm:space-y-10 lg:space-y-12 border border-gray-100 dark:border-gray-700 max-w-[1495px] ${
              user?.isBanned ? 'opacity-50 pointer-events-none' : ''
            }`} onSubmit={e => handleSubmit(e, 'published')}>
              {/* Product Details Section */}
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-2 sm:px-3 py-1 rounded-lg text-base sm:text-lg">1</span>
                  Product Details
                </h2>
                <div className="h-1 w-24 sm:w-32 bg-gradient-to-r from-red-400 to-blue-400 rounded-full mb-6 sm:mb-8" />
                <label className="block font-semibold mb-2 text-sm sm:text-base text-gray-900 dark:text-gray-100">Choose Account Type: <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                  {['facebook', 'instagram', 'tiktok', 'youtube'].map(type => {
                    // Platform color mapping
                    const colorMap = {
                      facebook: {
                        selected: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
                        check: 'from-blue-500 to-blue-400',
                      },
                      instagram: {
                        selected: 'border-pink-500 bg-pink-50 dark:bg-pink-900/20',
                        check: 'from-pink-500 to-pink-400',
                      },
                      tiktok: {
                        selected: 'border-gray-700 bg-gray-100 dark:bg-gray-700/20',
                        check: 'from-gray-700 to-gray-500',
                      },
                      youtube: {
                        selected: 'border-red-500 bg-red-50 dark:bg-red-900/20',
                        check: 'from-red-500 to-red-400',
                      },
                    };
                    const selected = form.accountType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`relative flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 shadow-sm hover:shadow-md hover:scale-105 focus:outline-none group
                          ${selected ? colorMap[type].selected : 'border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-[#404040] hover:bg-gray-100 dark:hover:bg-[#505050]'}`}
                        style={{ minHeight: '120px', minHeight: '140px' }}
                        onClick={() => setForm(f => ({ ...f, accountType: type }))}
                      >
                        {/* Animated checkmark for selected */}
                        {selected && (
                          <span className={`absolute top-2 sm:top-3 right-2 sm:right-3 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br ${colorMap[type].check} rounded-full flex items-center justify-center shadow-lg animate-bounce`}>
                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                          </span>
                        )}
                        <div className="mb-1 scale-110 group-hover:scale-125 transition-transform duration-200">
                          {platformIcons[type]}
                        </div>
                        <span className="font-bold capitalize mt-1 text-sm sm:text-base text-gray-900 dark:text-gray-100">{type}</span>
                      </button>
                    );
                  })}
                </div>
                {validationErrors.accountType && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.accountType}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Account Name (Product Title): <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input 
                        ref={fieldRefs.accountName}
                        className={`${modernInput} ${validationErrors.accountName ? 'border-red-500' : ''}`} 
                        value={form.accountName} 
                        onChange={e => {
                          setForm(f => ({ ...f, accountName: e.target.value }));
                          setValidationErrors(prev => ({ ...prev, accountName: '' }));
                        }} 
                        placeholder="e.g. Awesome YouTube Channel" 
                      />
                      {validationErrors.accountName && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.accountName}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Account URL: <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        ref={fieldRefs.accountUrl}
                        className={`${modernInput} ${validationErrors.accountUrl ? 'border-red-500' : ''}`}
                        value={form.accountUrl} 
                        onChange={e => {
                          setForm(f => ({ ...f, accountUrl: e.target.value }));
                          setValidationErrors(prev => ({ ...prev, accountUrl: '' }));
                        }} 
                        placeholder="e.g. https://instagram.com/youraccount" 
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 dark:text-gray-500 pointer-events-none">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 12H4" /></svg>
                      </span>
                    </div>
                    {validationErrors.accountUrl && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.accountUrl}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Price: <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">$</span>
                      <input
                        ref={fieldRefs.price}
                        className={`${modernInput} ${validationErrors.price ? 'border-red-500' : ''} pl-8`}
                        value={form.price} 
                        onChange={e => {
                          setForm(f => ({ ...f, price: e.target.value }));
                          setValidationErrors(prev => ({ ...prev, price: '' }));
                        }} 
                        placeholder="e.g. 500" 
                      />
                    </div>
                    {validationErrors.price && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.price}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Sale Price:</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">$</span>
                      <input
                        ref={fieldRefs.salePrice}
                        className={`${modernInput} ${validationErrors.salePrice ? 'border-red-500' : ''} pl-8`}
                        value={form.salePrice} 
                        onChange={e => {
                          setForm(f => ({ ...f, salePrice: e.target.value }));
                          setValidationErrors(prev => ({ ...prev, salePrice: '' }));
                        }} 
                        placeholder="e.g. 450" 
                      />
                    </div>
                    {validationErrors.salePrice && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.salePrice}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">Description (Short Description): <span className="text-red-500">*</span></label>
                  <textarea
                    ref={fieldRefs.description}
                    className={`${modernInput} ${validationErrors.description ? 'border-red-500' : ''}`}
                    rows={3}
                    value={form.description} 
                    onChange={e => {
                      setForm(f => ({ ...f, description: e.target.value }));
                      setValidationErrors(prev => ({ ...prev, description: '' }));
                    }} 
                    placeholder="Describe your account in a few words..." 
                  />
                  {validationErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.description}</p>
                  )}
                </div>
              </div>
              {/* Upload Images Section */}
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white px-2 sm:px-3 py-1 rounded-lg text-base sm:text-lg">2</span>
                  Upload Images
                </h2>
                <div className="h-1 w-24 sm:w-32 bg-gradient-to-r from-pink-400 to-yellow-400 rounded-full mb-6 sm:mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div>
                    <label className="block font-semibold mb-2 text-sm sm:text-base text-gray-900 dark:text-gray-100">Product Image *</label>
                    <div
                      ref={mainImageContainerRef}
                      className={`group relative flex flex-col items-center justify-center w-full h-40 sm:h-48 md:h-56 bg-gradient-to-br from-blue-50 to-white dark:from-[#3a3a3a] dark:to-[#2a2a2a] border-2 border-dashed rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${mainImageDragActive ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/20' : validationErrors.mainImage ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-blue-300 dark:border-blue-600 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg'} cursor-pointer mb-4`}
                      onClick={() => mainImageInputRef.current && mainImageInputRef.current.click()}
                      onDragOver={handleMainImageDragOver}
                      onDragLeave={handleMainImageDragLeave}
                      onDrop={handleMainImageDrop}
                    >
                      {mainImageDragActive && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-blue-100 dark:bg-blue-900/30 bg-opacity-80 rounded-xl sm:rounded-2xl z-10 pointer-events-none animate-pulse">
                          <svg className="w-12 h-12 sm:w-16 sm:h-16 text-blue-400 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                          <span className="font-bold text-blue-600 dark:text-blue-400 text-sm sm:text-lg">Drop main image here</span>
                        </div>
                      )}
                      {form.mainImagePreview && !mainImageDragActive ? (
                        <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-2">
                          <img src={form.mainImagePreview} alt="Main" className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-lg sm:rounded-xl shadow-md" />
                          <button
                            type="button"
                            className="absolute top-1 right-1 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 transition z-20 shadow"
                            onClick={e => { e.stopPropagation(); handleRemoveMainImage(); }}
                            aria-label="Remove main image"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
                              <line x1="6" y1="18" x2="18" y2="6" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                      ) : !mainImageDragActive ? (
                        <>
                          <div className="flex flex-col items-center">
                            <svg className="w-10 h-10 sm:w-14 sm:h-14 text-blue-400 group-hover:text-blue-500 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                            <span className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-lg text-center px-2">Click or Drag & Drop to upload main image</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">Recommended: 800x800px, JPG/PNG</span>
                          </div>
                        </>
                      ) : null}
                      <input type="file" accept="image/*" className="hidden" ref={mainImageInputRef} onChange={handleMainImageChange} />
                    </div>
                    {validationErrors.mainImage && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.mainImage}</p>
                    )}
                  </div>
                  <div>
                    <label className="block font-semibold mb-2 text-sm sm:text-base text-gray-900 dark:text-gray-100">Add More Photos (Min 3 - Max 15) *</label>
                    <div
                      ref={galleryImagesContainerRef}
                      className={`group relative flex flex-col items-center justify-center w-full h-40 sm:h-48 md:h-56 bg-gradient-to-br from-blue-50 to-white dark:from-[#3a3a3a] dark:to-[#2a2a2a] border-2 border-dashed rounded-xl sm:rounded-2xl transition-all duration-200 shadow-sm ${galleryDragActive ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/20' : validationErrors.galleryImages ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-blue-300 dark:border-blue-600 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg'} cursor-pointer`}
                      onClick={() => galleryInputRef.current && galleryInputRef.current.click()}
                      onDragOver={handleGalleryDragOver}
                      onDragLeave={handleGalleryDragLeave}
                      onDrop={handleGalleryDrop}
                    >
                      {galleryDragActive && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-blue-100 dark:bg-blue-900/30 bg-opacity-80 rounded-xl sm:rounded-2xl z-10 pointer-events-none animate-pulse">
                          <svg className="w-12 h-12 sm:w-16 sm:h-16 text-blue-400 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <rect x="3" y="5" width="18" height="14" rx="2" />
                            <path d="M3 17l6-6 4 4 8-8" />
                          </svg>
                          <span className="font-bold text-blue-600 dark:text-blue-400 text-sm sm:text-lg">Drop gallery images here</span>
                        </div>
                      )}
                      {form.galleryPreviews && form.galleryPreviews.length > 0 && !galleryDragActive ? (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {form.galleryPreviews.map((src, idx) => (
                            <div key={idx} className="relative w-16 h-16 sm:w-20 sm:h-20">
                              <img src={src} alt={`Gallery ${idx+1}`} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg shadow" />
                              <button
                                type="button"
                                className="absolute top-0.5 right-0.5 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 transition z-20 shadow"
                                onClick={e => { e.stopPropagation(); handleRemoveGalleryImage(idx); }}
                                aria-label={`Remove gallery image ${idx+1}`}
                              >
                                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
                                  <line x1="6" y1="18" x2="18" y2="6" strokeLinecap="round" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : !galleryDragActive ? (
                        <>
                          <div className="flex flex-col items-center">
                            <svg className="w-10 h-10 sm:w-14 sm:h-14 text-blue-400 group-hover:text-blue-500 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="3" y="5" width="18" height="14" rx="2" />
                              <path d="M3 17l6-6 4 4 8-8" />
                            </svg>
                            <span className="font-semibold text-gray-700 dark:text-gray-300 text-sm sm:text-lg text-center px-2">Click or Drag & Drop to upload gallery images</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">You can select multiple images</span>
                          </div>
                        </>
                      ) : null}
                      <input type="file" accept="image/*" multiple className="hidden" ref={galleryInputRef} onChange={handleGalleryChange} />
                    </div>
                    {validationErrors.galleryImages && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.galleryImages}</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Account Stats Section */}
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-2 sm:px-3 py-1 rounded-lg text-base sm:text-lg">3</span>
                  Account Stats
                </h2>
                <div className="h-1 w-24 sm:w-32 bg-gradient-to-r from-blue-400 to-green-400 rounded-full mb-6 sm:mb-8" />
                <button type="button" className="mb-4 sm:mb-6 px-4 sm:px-6 py-2 rounded-full bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition text-sm sm:text-base">Auto Fill</button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4">
                  <div>
                    <input
                      ref={fieldRefs.followers}
                      className={`${modernInput} ${validationErrors.followers ? 'border-red-500' : ''}`}
                      placeholder="Total Followers"
                      value={form.stats.followers}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, followers: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, followers: '' }));
                      }}
                    />
                    {validationErrors.followers && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.followers}</p>
                    )}
                  </div>
                  <div>
                    <select
                      ref={fieldRefs.monetized}
                      className={`${modernInput} ${validationErrors.monetized ? 'border-red-500' : ''}`}
                      value={form.stats.monetized}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, monetized: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, monetized: '' }));
                      }}
                    >
                      <option value="">Monetized?</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                    {validationErrors.monetized && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.monetized}</p>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fieldRefs.income}
                      className={`${modernInput} ${validationErrors.income ? 'border-red-500' : ''}`}
                      placeholder="Monthly Income"
                      value={form.stats.income}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, income: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, income: '' }));
                      }}
                    />
                    {validationErrors.income && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.income}</p>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fieldRefs.videos}
                      className={`${modernInput} ${validationErrors.videos ? 'border-red-500' : ''}`}
                      placeholder="Total Number Of Videos"
                      value={form.stats.videos}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, videos: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, videos: '' }));
                      }}
                    />
                    {validationErrors.videos && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.videos}</p>
                    )}
                  </div>
                  <div>
                    <select
                      ref={fieldRefs.category}
                      className={`${modernInput} ${validationErrors.category ? 'border-red-500' : ''}`}
                      value={form.stats.category}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, category: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, category: '' }));
                      }}
                    >
                      <option value="">Account Category</option>
                      <option value="Education & QA">Education & QA</option>
                      <option value="Reviews & How to">Reviews & How to</option>
                      <option value="Movies & Music">Movies & Music</option>
                      <option value="Games">Games</option>
                      <option value="Humor">Humor</option>
                      <option value="Cars & Bikes">Cars & Bikes</option>
                      <option value="Crypto & NFT">Crypto & NFT</option>
                      <option value="Fashion & style">Fashion & style</option>
                      <option value="Fitness & Sports">Fitness & Sports</option>
                      <option value="Food">Food</option>
                      <option value="Luxury & Motivation">Luxury & Motivation</option>
                      <option value="Models & Celebrities">Models & Celebrities</option>
                      <option value="Nature">Nature</option>
                      <option value="Pets & Animals">Pets & Animals</option>
                      <option value="Travel">Travel</option>
                      <option value="YT Shorts & FB reels">YT Shorts & FB reels</option>
                    </select>
                    {validationErrors.category && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.category}</p>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fieldRefs.views}
                      className={`${modernInput} ${validationErrors.views ? 'border-red-500' : ''}`}
                      placeholder="Total Number Of Views"
                      value={form.stats.views}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, views: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, views: '' }));
                      }}
                    />
                    {validationErrors.views && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.views}</p>
                    )}
                  </div>
                  <div>
                    <select
                      ref={fieldRefs.strikes}
                      className={`${modernInput} ${validationErrors.strikes ? 'border-red-500' : ''}`}
                      value={form.stats.strikes}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, strikes: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, strikes: '' }));
                      }}
                    >
                      <option value="">Strikes or violation?</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                    {validationErrors.strikes && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.strikes}</p>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fieldRefs.created}
                      className={`${modernInput} ${validationErrors.created ? 'border-red-500' : ''}`}
                      placeholder="Account Created"
                      value={form.stats.created}
                      onChange={e => {
                        setForm(f => ({ ...f, stats: { ...f.stats, created: e.target.value } }));
                        setValidationErrors(prev => ({ ...prev, created: '' }));
                      }}
                    />
                    {validationErrors.created && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.created}</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Show general error message only if there are validation errors */}
              {Object.keys(validationErrors).length > 0 && (
                <div className="text-red-600 font-semibold mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  Please fill in all required fields highlighted in red.
                </div>
              )}
              {error && <div className="text-red-600 font-semibold mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg whitespace-pre-line">{error}</div>}
              <div className="flex gap-4 mt-6 sm:mt-8">
                {user?.isBanned ? (
                  <button 
                    type="button" 
                    className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-400 text-white rounded-full font-bold cursor-not-allowed transition text-sm sm:text-base"
                    disabled
                  >
                    Publish (Disabled - Account Suspended)
                  </button>
                ) : (
                <button 
                  type="submit" 
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 transition text-sm sm:text-base ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Publishing...' : 'Publish'}
                </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default SellAccount; 